/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { merge, Observable, Subscription } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { OptionMultiSelectionService } from '../../option/selection/multi-selection/option-multi-selection.service';
import { SelectComponent } from '../select.component';

@Component({
    selector: 'ngf-multi-select,ngf-multi-select[small]',
    templateUrl: './multi-select.component.html',
    styleUrls: ['./multi-select.component.scss'],
    standalone: false
})
export class MultiSelectComponent implements OnInit, OnDestroy {

  @Input()
  legacyMultiselect = false;

  @Input()
  ariaLabel?: string;

  @Input()
  ariaLabelledby?: string;

  @Input()
  ariaDescribedby?: string;

  readonly values$: Observable<any[]> = this.optionService.selection$.pipe(map(it => it?.values ?? []));
  readonly notEmpty$: Observable<boolean> = this.values$.pipe(map(it => it?.length > 0));
  readonly selectedLabels$: Observable<string> = this.values$.pipe(
    map(values => {
      const labels = this.optionService.findOptionsByValues(values).map(o => o.textContent);
      if (labels.length <= 2) {
        return labels.join(', ');
      }
      return `${labels[0]}, and ${labels.length - 1} more`;
    })
  );

  constructor(public select: SelectComponent,
              private optionService: OptionMultiSelectionService,
              private elementRef: ElementRef<HTMLElement>) {
  }

  private sub: Subscription;

  ngOnInit(): void {
    this.sub = merge(
      this.handleSelectionChange$(),
      this.handleWrittenValueChanged$()
    ).subscribe();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  @HostListener('click', ['$event'])
  handleClick(event: Event) {
    const target = event.target as HTMLElement;
    const nativeEl = this.elementRef.nativeElement;
    // Don't intercept clicks from interactive child elements (pill remove buttons, +X overflow
    // button). Those elements call stopPropagation themselves; this guard is a fallback to prevent
    // double-toggling the options overlay when the keyboard synthesises a click on those buttons.
    if (target !== nativeEl && target.matches('button, a[href], [role="button"]')) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    this.select.optionsOverlay.toggle();
  }

  handleBlur() {
    this.select.onTouched();
  }

  handleRemove(value: any) {
    this.optionService.select({
      value,
      userInput: true
    });
    this.select.optionsOverlay.close();
  }

  handleClearAll() {
    this.optionService.clear({
      userInput: true
    });
    this.select.optionsOverlay.close();
  }
  private handleSelectionChange$() {
    return this.optionService.selection$.pipe(
      filter(selection => selection.userInput),
      tap(selection => {
        this.select.handleSelection(selection.values);
      })
    );
  }

  private handleWrittenValueChanged$() {
    return this.select.writtenValue$.pipe(
      tap(values => this.optionService.selectAll({values, userInput: false}))
    );
  }

}
